/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SlideData {
  id: number;
  badge: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  image: string;
}

const Carousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const slides: SlideData[] = [
    {
      id: 2,
      badge: t("hero_badge"),
      title: `${t("les")} ${t("prix")} ${t("marche_reel")}`,
      subtitle: t("slide1_description"),
      ctaText: t("consulter"),
      ctaLink: "/consulter",
      image: "/assets/slide1.png",
    },
    {
      id: 3,
      badge: t("hero_badge"),
      title: `${t("des_produits")} ${t("frais")} ${t("cultive_avec")} ${t("passion")}`,
      subtitle: t("text_slide"),
      ctaText: t("savoir_plus"),
      ctaLink: "/list_products",
      image: "/assets/agricoles.png",
    },
    {
      id: 4,
      badge: t("hero_badge"),
      title: `${t("un")} ${t("transporteur")} ${t("en_quelque")} ${t("click")}`,
      subtitle: t("text_slide2"),
      ctaText: t("search"),
      ctaLink: "/recherche_trans/0",
      image: "/assets/deliver1.png",
    },
    {
      id: 5,
      badge: t("hero_badge"),
      title: `${t("poissons")} ${t("frais_produit_de")} ${t("peche")}`,
      subtitle: t("text_slide4"),
      ctaText: t("search"),
      ctaLink: "/list_products",
      image: "/assets/HALIEUTIQUE.png",
    },
    {
      id: 6,
      badge: t("hero_badge"),
      title: t("intrat"),
      subtitle: t("text_slide6"),
      ctaText: t("search"),
      ctaLink: "/list_products",
      image: "/assets/INTRATS_AGRICOLES.png",
    },
    {
      id: 7,
      badge: t("hero_badge"),
      title: t("elevage"),
      subtitle: t("text_slide7"),
      ctaText: t("search"),
      ctaLink: "/list_products",
      image: "/assets/ELEVAGE.png",
    },
    {
      id: 8,
      badge: t("hero_badge"),
      title: t("stock_produit"),
      subtitle: t("slogant_stock"),
      ctaText: t("a_venir"),
      ctaLink: "/comming_stock",
      image: "/assets/Stock_image.png",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const currentSlide = slides[currentIndex];

  return (
    <div className="relative w-full min-h-[480px] sm:min-h-[520px] lg:h-[700px] max-h-[880px] overflow-hidden flex items-center bg-gray-900">
      {/* Background Image full-bleed with smooth transition */}
      <div
        key={currentIndex}
        className="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-out animate-zoomIn"
        style={{ backgroundImage: `url(${currentSlide.image})` }}
      />

      {/* Background Overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-black/15 to-transparent" />

      {/* Floating Card Container */}
      <div className="relative z-10 container mx-auto px-4 sm:px-8 md:px-12 lg:px-16 py-6 sm:py-10 w-full">
        <div
          key={`card-${currentIndex}`}
          className="max-w-[450px] w-full bg-white/95 sm:bg-white backdrop-blur-md rounded-2xl shadow-2xl p-5 sm:p-7 relative overflow-hidden transition-all duration-500 animate-slideUp -rotate-1 sm:-rotate-2 hover:rotate-0 transform"
        >
          {/* Vertical Accent Stripe on Left edge (Orange top -> Green bottom) */}
          <div className="absolute left-0 top-0 bottom-0 w-[5px] bg-gradient-to-b from-[#ff7a00] via-[#ff7a00] to-[#15803d] rounded-l-2xl" />

          {/* Badge line */}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-[#ff7a00] shrink-0" />
            <span className="text-[11px] sm:text-xs font-bold tracking-[0.16em] text-[#15803d] uppercase font-sans">
              {currentSlide.badge}
            </span>
          </div>

          {/* Main Title */}
          <h1 className="text-xl sm:text-2xl lg:text-[1.85rem] font-extrabold text-gray-900 leading-[1.2] tracking-tight mb-3 font-sans">
            {currentSlide.title}
          </h1>

          {/* Subtitle */}
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 italic font-normal leading-relaxed mb-4 font-sans">
            {currentSlide.subtitle}
          </p>

          {/* Divider */}
          <div className="w-full h-px bg-gray-100 mb-4" />

          {/* Location & Year */}
          <div className="flex items-center gap-2 text-[11px] sm:text-xs text-gray-500 font-medium mb-5 font-sans">
            <MapPin className="w-3.5 h-3.5 text-[#15803d]" />
            <span>{t("hero_location") || "Cameroun"}</span>
            <span className="text-gray-300">-</span>
            <span>{t("hero_year") || "2026"}</span>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(currentSlide.ctaLink)}
              className="group inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-gray-900 hover:bg-black text-white rounded-full font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg"
            >
              <span>{currentSlide.ctaText}</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate("/list_products")}
              className="text-[#15803d] hover:text-green-800 font-bold text-xs sm:text-sm transition-colors px-2 py-1 hover:underline font-sans"
            >
              {t("hero_cta_secondary") || "Nous découvrir"}
            </button>
          </div>
        </div>
      </div>

      {/* Carousel Dots */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`transition-all duration-300 ${
              currentIndex === index
                ? 'w-7 h-2 bg-white rounded-full shadow-lg'
                : 'w-2 h-2 bg-white/50 hover:bg-white/80 rounded-full'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(15px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes zoomIn {
      from { opacity: 0.7; transform: scale(1.03); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-slideUp {
      animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .animate-zoomIn {
      animation: zoomIn 0.8s ease-out forwards;
    }
  `;
  document.head.appendChild(styleSheet);
}

export default Carousel;

